import type { LoaderFunctionArgs } from '~/types/route-args';
import { data as routerData } from 'react-router';
import { getRecentlyViewedServer } from '~/modules-rocco/recently-viewed/api/get-recently-viewed.server';
import { getRecentlyViewedAllLiteServer } from '~/modules-rocco/recently-viewed/api/get-recently-viewed-all-lite.server';
import { mapRecentlyViewedLiteToTileViewModel } from '~/modules-rocco/recently-viewed/data-mappers/tile-generic';
import { mapProductToTileViewModel } from '@modules-rocco/products/data-mappers/tile-product';

export const RECENTLY_VIEWED_ACTION = '/actions/recently-viewed';

type RecentlyViewedType = 'Products' | 'All';

/** The recently-viewed rail renders at most this many mixed tiles. */
const RAIL_LIMIT = 8;

/**
 * API endpoint to get the user's most recently viewed items. Use with the
 * `useRecentlyViewed` hook.
 *
 * Query params:
 * - type: 'Products' | 'All' (default: 'All')
 *   - 'Products': Returns ProductTileViewModel[] in `recentlyViewedProducts`
 *   - 'All': Returns TileViewModel[] in `recentlyViewedItems`
 *
 * @see {@link packages/web/app/modules-rocco/recently-viewed/hooks/use-recently-viewed.ts}
 */
export const loader = async ({ request, context }: LoaderFunctionArgs) => {
    const { appDisplayMode, user, logger } = context;
    const url = new URL(request.url);
    const typeParam = url.searchParams.get('type');
    const type: RecentlyViewedType =
        typeParam === 'Products' ? 'Products' : 'All';

    try {
        // Products pages need full product tiles (pricing etc.), so keep the heavy query there.
        if (type === 'Products') {
            const recentlyViewed = await getRecentlyViewedServer(
                { type },
                context,
            );
            const products =
                recentlyViewed.RecentlyViewedProducts?.map((product) =>
                    mapProductToTileViewModel(product, user, {
                        appDisplayMode,
                    }),
                ) ?? [];

            return {
                recentlyViewedProducts: products,
                recentlyViewedItems: null,
            };
        }

        // The rail only renders carousel tiles, so use the lighter query and cap at the rail size.
        const recentlyViewed = await getRecentlyViewedAllLiteServer(
            { limit: RAIL_LIMIT },
            context,
        );

        const recentlyViewedTiles = recentlyViewed.RecentlyViewedAll.map(
            (item) =>
                mapRecentlyViewedLiteToTileViewModel(
                    item,
                    user,
                    appDisplayMode,
                ),
        );

        return {
            recentlyViewedItems: recentlyViewedTiles,
            recentlyViewedProducts: null,
        };
    } catch (error) {
        logger.error('Error in /actions/recently-viewed', { error, type });
        return routerData(null, { status: 500 });
    }
};
